import { AlertTriangle, CheckCircle2, Info } from 'lucide-react'
import {
  buildReferenceReportViewModel,
  normalizeSeverity,
  severityLabel,
  type ReportFinding,
} from './reportViewModel'

const SEVERITY_STYLES: Record<number, { label: string; color: string; icon: typeof AlertTriangle }> = {
  1: { label: 'Low', color: 'text-amber-600 bg-amber-50 border-amber-200', icon: Info },
  2: { label: 'Moderate', color: 'text-orange-600 bg-orange-50 border-orange-200', icon: AlertTriangle },
  3: { label: 'High', color: 'text-rose-600 bg-rose-50 border-rose-200', icon: AlertTriangle },
}

function severityStyle(finding: ReportFinding) {
  const severity = normalizeSeverity(finding.severity)
  return SEVERITY_STYLES[severity] ?? SEVERITY_STYLES[1]
}

function findingText(finding: ReportFinding): string {
  return finding.observation || finding.summary || 'No finding narrative recorded.'
}

export function ReportViewer({ payload }: { payload: Record<string, unknown> | null }) {
  const report = buildReferenceReportViewModel(payload)
  if (!report) return <p className="text-sm text-slate-400">No report data available.</p>

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <p className="text-xs font-black uppercase tracking-widest text-slate-500">Report Cover</p>
        <h3 className="mt-2 font-brand text-xl font-bold text-[var(--brand-deep)]">{report.cover.title}</h3>
        <p className="mt-1 text-sm font-semibold text-slate-700">Facility: {report.cover.facilityName}</p>
        {report.cover.address && <p className="text-sm text-slate-500">{report.cover.address}</p>}
        <dl className="mt-3 grid gap-2 text-xs text-slate-600 sm:grid-cols-2">
          <div>
            <dt className="font-bold uppercase text-slate-400">Survey Date</dt>
            <dd>{report.cover.surveyDate ?? 'Date not set'}</dd>
          </div>
          <div>
            <dt className="font-bold uppercase text-slate-400">Visit Type</dt>
            <dd>{report.cover.visitType ?? 'Mock survey'}</dd>
          </div>
          <div>
            <dt className="font-bold uppercase text-slate-400">Framework</dt>
            <dd>{report.cover.accreditationFrameworkLabel}</dd>
          </div>
          <div>
            <dt className="font-bold uppercase text-slate-400">Report Version</dt>
            <dd>{report.cover.reportVersion ? `v${report.cover.reportVersion}` : 'Draft'}</dd>
          </div>
          <div>
            <dt className="font-bold uppercase text-slate-400">Generated</dt>
            <dd>{report.cover.generatedAt ?? 'Pending'}</dd>
          </div>
        </dl>
      </section>

      {report.outcomeLabel && (
        <div className="flex items-center gap-3 rounded-xl bg-slate-50 p-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--brand-light)]">
            {report.outcomeCode === 'meets_standard' ? (
              <CheckCircle2 size={24} className="text-[var(--brand-secondary)]" />
            ) : (
              <AlertTriangle size={24} className={report.outcomeCode === 'at_risk' ? 'text-rose-500' : 'text-amber-500'} />
            )}
          </div>
          <div>
            <p className="font-brand text-lg font-bold text-[var(--brand-deep)]">
              {report.outcomeLabel}
            </p>
            {typeof report.score.overall_score === 'number' && (
              <p className="text-sm text-slate-500">
                Overall Score: {report.score.overall_score}{report.score.grade ? ` | Grade ${report.score.grade}` : ''}
              </p>
            )}
          </div>
        </div>
      )}

      <section>
        <h3 className="text-xs font-black uppercase tracking-widest text-slate-500">Executive Summary</h3>
        <p className="mt-2 text-sm leading-relaxed text-slate-700">{report.executiveSummary}</p>
      </section>

      <section>
        <h3 className="text-xs font-black uppercase tracking-widest text-slate-500">Immediate Attention</h3>
        {report.immediateAttention.length > 0 ? (
          <div className="mt-3 space-y-2">
            {report.immediateAttention.map((finding, index) => (
              <div key={finding.finding_id ?? index} className="rounded-lg border border-rose-200 bg-rose-50 p-3">
                <p className="text-xs font-bold text-rose-700">
                  {severityLabel(finding.severity)} - {finding.standard_ref || finding.standard_id || 'Standard pending'}
                </p>
                <p className="mt-1 text-sm leading-relaxed text-rose-900">{findingText(finding)}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-2 text-sm text-slate-500">No immediate-attention findings were identified in this report payload.</p>
        )}
      </section>

      <section>
        <h3 className="text-xs font-black uppercase tracking-widest text-slate-500">Domain Summary</h3>
        {report.domains.length > 0 ? (
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {report.domains.map((domain) => (
              <div key={domain.code} className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-center gap-2">
                  <span className="rounded bg-[var(--brand-light)] px-2 py-0.5 text-xs font-extrabold text-[var(--brand-primary)]">
                    {domain.code}
                  </span>
                  <p className="text-sm font-bold text-[var(--brand-deep)]">{domain.label}</p>
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  {domain.findings.length} finding{domain.findings.length === 1 ? '' : 's'} - {domain.highCount} high, {domain.moderateCount} moderate, {domain.lowCount} low
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-2 text-sm text-slate-500">No domain findings were included in this report payload.</p>
        )}
      </section>

      <section>
        <h3 className="text-xs font-black uppercase tracking-widest text-slate-500">
          Consolidated Findings ({report.totalFindings})
        </h3>
        {report.domains.length > 0 ? (
          <div className="mt-3 space-y-4">
            {report.domains.map((domain) => (
              <div key={domain.code}>
                <h4 className="mb-2 text-sm font-bold text-[var(--brand-deep)]">{domain.label}</h4>
                <div className="space-y-2">
                  {domain.findings.map((finding, index) => {
                    const sev = severityStyle(finding)
                    const Icon = sev.icon
                    const standardRef = finding.standard_ref || finding.standard_id
                    return (
                      <div key={finding.finding_id ?? index} className={`rounded-lg border p-3 ${sev.color}`}>
                        <div className="flex items-start gap-2">
                          <Icon size={14} className="mt-0.5 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold">{sev.label}</span>
                              {finding.area && <span className="text-xs opacity-60">{finding.area}</span>}
                              {standardRef && <span className="text-xs opacity-60">{standardRef}</span>}
                              {finding.condition_level && <span className="text-xs opacity-60">{String(finding.condition_level)}</span>}
                            </div>
                            <p className="mt-1 text-xs leading-relaxed opacity-80">{findingText(finding)}</p>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-3 rounded-lg border border-dashed border-emerald-200 bg-emerald-50 p-4 text-center">
            <CheckCircle2 size={24} className="mx-auto text-emerald-500" />
            <p className="mt-2 text-sm font-semibold text-emerald-700">No deficiencies found</p>
          </div>
        )}
      </section>

      <section>
        <h3 className="text-xs font-black uppercase tracking-widest text-slate-500">Action Plan Status</h3>
        {report.correctiveActions.length > 0 ? (
          <div className="mt-3 space-y-2">
            {report.correctiveActions.map((action, index) => (
              <div key={action.action_id ?? index} className="rounded-lg border border-slate-200 p-3">
                <p className="text-sm font-bold text-slate-800">{action.title || action.description || 'Corrective action'}</p>
                <p className="mt-1 text-xs text-slate-500">
                  Status: {action.status || 'not set'}{action.due_date ? ` | Due ${action.due_date}` : ''}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-2 text-sm text-slate-500">No corrective action plan items were included in this report payload.</p>
        )}
      </section>

      <section>
        <h3 className="text-xs font-black uppercase tracking-widest text-slate-500">Appendix - Mock Survey Images and Issues</h3>
        {report.appendixItems.length > 0 ? (
          <div className="mt-3 space-y-2">
            {report.appendixItems.map((finding, index) => (
              <div key={finding.finding_id ?? index} className="rounded-lg border border-slate-200 p-3">
                <p className="text-sm font-semibold text-slate-800">{finding.finding_id || `Appendix item ${index + 1}`}</p>
                <p className="mt-1 text-xs text-slate-500">{findingText(finding)}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-2 text-sm text-slate-500">No image appendix items were included in this report payload.</p>
        )}
      </section>
    </div>
  )
}
